import statesRaw from '@/data/ic3-2025-states.json'
import nationalRaw from '@/data/ic3-2025-national.json'
import insightsRaw from '@/data/ic3-2025-state-insights.json'

export type IC3StateRow = {
  name: string
  complaints: number
  losses: number
  complaintsPer100k: number
  lossesPer100k: number
  complaintsRank: number
  lossesRank: number
  elder60Plus: { complaints: number; losses: number }
  youthUnder18: { complaints: number; losses: number }
  cryptoComplaints: number
  cryptoLosses: number
}

export type IC3StateInsights = {
  name: string
  avgLossPerComplaint: number
  elderPctOfComplaints: number
  elderPctOfLosses: number
  cryptoPctOfLosses: number
  lossesPerCapitaRank: number
  complaintsPerCapitaRank: number
}

const STATES = (statesRaw as { states: Record<string, IC3StateRow> }).states
const INSIGHTS = (insightsRaw as { states: Record<string, IC3StateInsights> }).states

type TransactionMethod = { method: string; percent: number }
type CrimeTypeRow = { type: string; losses: number }
const NATIONAL = nationalRaw as {
  meta: { reportYear: number }
  headline?: {
    totalComplaints?: number
    totalLosses?: number
    averageLoss?: number
    yoyLossIncrease?: number
    complaintsPerDay?: number
  }
  topCrimeTypesByLoss?: CrimeTypeRow[]
  topCrimeTypesByCount?: Array<{ type: string; complaints: number }>
  transactionMethodsByCrimeType?: Record<string, TransactionMethod[]>
}

/** Return the IC3 row for a 2-letter code or slug ("ny" / "NY" / "new-york"). */
export function getStateRow(input: string): IC3StateRow | null {
  const code = resolveCode(input)
  if (!code) return null
  return STATES[code] ?? null
}

export function getStateInsights(input: string): IC3StateInsights | null {
  const code = resolveCode(input)
  if (!code) return null
  return INSIGHTS[code] ?? null
}

export function getReportYear(): number {
  return NATIONAL.meta?.reportYear ?? 2025
}

export function listAllStates(): Array<{ code: string; slug: string; row: IC3StateRow }> {
  return Object.entries(STATES)
    .map(([code, row]) => ({ code, slug: nameToSlug(row.name), row }))
    .sort((a, b) => a.row.name.localeCompare(b.row.name))
}

export function nameToSlug(name: string): string {
  return name.toLowerCase().replace(/\s+/g, '-')
}

export function slugToCode(slug: string): string | null {
  // Apply the same normalization as nameToSlug so we accept either form:
  //   "MD" / "md" / "maryland" / "Maryland"     → MD
  //   "West Virginia" / "west-virginia" / "wv"  → WV
  // Without this, multi-word names with spaces (e.g. "West Virginia"
  // straight from us-atlas) fail to match the hyphenated slug form.
  const normalized = slug.toLowerCase().replace(/\s+/g, '-')
  if (normalized.length === 2 && STATES[normalized.toUpperCase()]) {
    return normalized.toUpperCase()
  }
  for (const [code, row] of Object.entries(STATES)) {
    if (nameToSlug(row.name) === normalized) return code
  }
  return null
}

export function codeToSlug(code: string): string | null {
  const row = STATES[code.toUpperCase()]
  return row ? nameToSlug(row.name) : null
}

function resolveCode(input: string): string | null {
  if (!input) return null
  if (input.length === 2 && STATES[input.toUpperCase()]) return input.toUpperCase()
  return slugToCode(input)
}

export function formatLossUSD(usd: number): string {
  if (usd >= 1_000_000_000) return `$${(usd / 1_000_000_000).toFixed(1)}B`
  if (usd >= 1_000_000) return `$${Math.round(usd / 1_000_000)}M`
  if (usd >= 1_000) return `$${Math.round(usd / 1_000)}K`
  return `$${usd.toLocaleString()}`
}

/**
 * Picks a single attention-grabbing stat for a state, using whichever
 * dimension this state ranks notably on. Used in headlines on state pages.
 */
export function getHeadlineInsight(code: string): string | null {
  const row = STATES[code.toUpperCase()]
  const insight = INSIGHTS[code.toUpperCase()]
  if (!row || !insight) return null

  if (insight.lossesPerCapitaRank <= 5) {
    return `Among the highest per-capita fraud losses in the country: ${formatLossUSD(row.lossesPer100k)} per 100k residents.`
  }
  if (insight.elderPctOfLosses >= 50) {
    return `Adults 60+ account for ${insight.elderPctOfLosses.toFixed(0)}% of fraud losses in ${row.name}.`
  }
  if (insight.cryptoPctOfLosses >= 55) {
    return `Cryptocurrency was involved in ${insight.cryptoPctOfLosses.toFixed(0)}% of reported losses in ${row.name}.`
  }
  if (row.lossesRank <= 5) {
    return `${row.name} ranked #${row.lossesRank} nationally for total reported fraud losses.`
  }
  return `${row.name} residents reported ${formatLossUSD(row.losses)} in fraud losses to the FBI in ${getReportYear()}.`
}

/**
 * Long-form key insight (2 sentences) for the callout panel on state pages.
 * Every claim is either a direct figure from the state's IC3 row or
 * arithmetic on those figures, no inferred causation, no extrapolation
 * from national patterns to state-specific narrative.
 */
export function getKeyInsightNarrative(code: string): string | null {
  const row = STATES[code.toUpperCase()]
  const insight = INSIGHTS[code.toUpperCase()]
  if (!row || !insight) return null

  const elderPct = Math.round(insight.elderPctOfLosses)
  const elderComplaintsPct = Math.round(insight.elderPctOfComplaints)
  const cryptoPct = Math.round(insight.cryptoPctOfLosses)
  const cryptoComplaintsPct = Math.round(
    (row.cryptoComplaints / row.complaints) * 100,
  )

  // National baselines published in NATIONAL.headline (IC3 2025).
  const NATIONAL_AVG_LOSS = NATIONAL.headline?.averageLoss ?? 20_699
  const NATIONAL_TOTAL_LOSSES = NATIONAL.headline?.totalLosses ?? 20_877_000_000

  /**
   * Per-case "X times more costly" multiplier comparing one slice of cases
   * to the rest, given that slice's share of dollars and share of cases.
   * Returns null when the slice is empty or the whole.
   */
  const multiplier = (sliceDollars: number, sliceCases: number) => {
    if (sliceCases <= 0 || sliceCases >= 1) return null
    return (
      (sliceDollars / sliceCases) /
      ((1 - sliceDollars) / (1 - sliceCases))
    )
  }

  const elderMult = multiplier(
    insight.elderPctOfLosses / 100,
    insight.elderPctOfComplaints / 100,
  )
  const cryptoMult = multiplier(
    insight.cryptoPctOfLosses / 100,
    row.cryptoComplaints / row.complaints,
  )

  if (elderPct >= 45 && elderMult && elderMult >= 1.5) {
    return `Adults aged 60+ account for ${elderPct}% of fraud losses in ${row.name}, even though they file just ${elderComplaintsPct}% of complaints. The gap reflects per-case severity: cases targeting older residents run roughly ${elderMult.toFixed(1)}× the cost of cases targeting younger ones.`
  }
  if (cryptoPct >= 55 && cryptoMult && cryptoMult >= 2) {
    const x = cryptoMult >= 5 ? Math.round(cryptoMult) : cryptoMult.toFixed(1)
    return `Cryptocurrency was involved in ${cryptoPct}% of ${row.name} fraud losses but just ${cryptoComplaintsPct}% of complaints. The gap reflects per-case severity: each crypto-involved case averages roughly ${x}× the cost of other cases, and once funds move through a wallet they're effectively unrecoverable.`
  }
  if (insight.lossesPerCapitaRank <= 5) {
    return `${row.name} residents lose more to fraud per capita than nearly every other state, ${formatLossUSD(row.lossesPer100k)} per 100,000 people. The state's average reported case runs ${formatLossUSD(insight.avgLossPerComplaint)}, against ${formatLossUSD(NATIONAL_AVG_LOSS)} nationally.`
  }
  if (row.lossesRank <= 5) {
    const everyN = Math.round(NATIONAL_TOTAL_LOSSES / row.losses)
    return `${row.name} ranks #${row.lossesRank} nationally for total reported fraud losses. The state alone accounts for roughly $1 of every $${everyN} lost to internet-enabled fraud in the US in ${getReportYear()}.`
  }
  // Default fallback: spell out the per-capita rank in plain terms.
  // lossesPer100k is reported in dollars per 100,000 residents, divide
  // back down to a per-person figure that's easier to feel.
  const perPerson = Math.round(row.lossesPer100k / 100_000)
  return `${row.name} residents lose roughly $${perPerson.toLocaleString()} per person each year to internet-enabled fraud, placing the state #${insight.lossesPerCapitaRank} of 51 nationally for per-capita losses. The average reported case here ran ${formatLossUSD(insight.avgLossPerComplaint)}, against ${formatLossUSD(NATIONAL_AVG_LOSS)} nationally.`
}

/**
 * Returns 4 stat-led insights for the given state, derived from the IC3
 * dataset. Universally available for all 50 + DC (no curated content
 * needed). Each insight has a punchy `headline` (the big number) and a
 * supporting `body` paragraph.
 */
export type KeyInsight = { headline: string; body: string }

export function getKeyInsights(code: string): KeyInsight[] {
  const row = STATES[code.toUpperCase()]
  const ins = INSIGHTS[code.toUpperCase()]
  if (!row || !ins) return []

  // Rank this state's crypto-loss share against all states/DC.
  const cryptoRanked = Object.entries(INSIGHTS)
    .map(([c, i]) => ({ code: c, pct: i.cryptoPctOfLosses }))
    .sort((a, b) => b.pct - a.pct)
  const cryptoRank =
    cryptoRanked.findIndex(r => r.code === code.toUpperCase()) + 1

  const elderPct = Math.round(ins.elderPctOfLosses)
  const elderComplaintsPct = Math.round(ins.elderPctOfComplaints)
  const cryptoPct = Math.round(ins.cryptoPctOfLosses)

  let cryptoBody = `of ${row.name} fraud losses involved cryptocurrency.`
  if (cryptoRank === 1) cryptoBody += ' The highest share in the country.'
  else if (cryptoRank <= 3)
    cryptoBody += ` Among the top ${ordinalShort(cryptoRank)} highest in the country.`
  else if (cryptoRank <= 10)
    cryptoBody += ` That puts ${row.name} in the top ten nationally.`

  return [
    {
      headline: `${elderPct}%`,
      body: `of ${row.name}'s fraud dollars are lost by residents 60 and older, even though they file just ${elderComplaintsPct}% of complaints.`,
    },
    {
      headline: `${cryptoPct}%`,
      body: cryptoBody,
    },
    {
      headline: `#${ins.lossesPerCapitaRank}`,
      body: `of 51 for per-capita fraud losses, ${formatLossUSD(row.lossesPer100k)} lost per 100,000 residents.`,
    },
    {
      headline: formatLossUSD(ins.avgLossPerComplaint),
      body: `the average loss per reported case in ${row.name}.`,
    },
  ]
}

function ordinalShort(n: number): string {
  if (n === 1) return '1st'
  if (n === 2) return '2nd'
  if (n === 3) return '3rd'
  return `${n}th`
}

/** National top crime types by losses, capped at `limit`. */
export function getNationalTopCrimeTypes(limit = 5): CrimeTypeRow[] {
  return (NATIONAL.topCrimeTypesByLoss ?? []).slice(0, limit)
}

/** National headline figures used on the homepage and the state page footer note. */
export function getNationalHeadline() {
  return NATIONAL.headline ?? {}
}

/**
 * Aggregate payment methods across all top crime types, weighted by loss
 * volume. Returns a representative "how money moves in fraud" picture
 * with integer percentages that sum to exactly 100.
 *
 * Note: this is national data. The FBI publishes payment-method splits
 * only by crime type (national level), not by state.
 */
export function getNationalPaymentMethods(): TransactionMethod[] {
  const methods = NATIONAL.transactionMethodsByCrimeType ?? {}
  const crimeMap: Record<string, number> = {
    investment: 8_648_617_756,
    businessEmailCompromise: 3_046_598_558,
    techSupport: 2_134_675_818,
    confidenceRomance: 929_287_469,
    governmentImpersonation: 797_943_193,
    extortion: 130_000_000,
  }

  const totals: Record<string, number> = {}
  let totalWeight = 0

  for (const [crime, weight] of Object.entries(crimeMap)) {
    const rows = methods[crime]
    if (!rows) continue
    totalWeight += weight
    for (const { method, percent } of rows) {
      const key = normalizeMethod(method)
      totals[key] = (totals[key] ?? 0) + (percent * weight) / 100
    }
  }

  if (totalWeight === 0) return []

  // Convert weighted sums to float percentages, keeping all categories
  // including "Other" so the totals are honest.
  const raw = Object.entries(totals).map(([method, weighted]) => ({
    method,
    pctFloat: (weighted / totalWeight) * 100,
  }))

  // Largest-remainder rounding: floor each, then distribute the leftover
  // points to whichever categories had the largest fractional parts.
  // Result: integer percentages that sum to exactly 100.
  const out = raw.map(r => ({ method: r.method, percent: Math.floor(r.pctFloat) }))
  let remainder = 100 - out.reduce((s, r) => s + r.percent, 0)
  const byFraction = raw
    .map((r, i) => ({ i, frac: r.pctFloat - Math.floor(r.pctFloat) }))
    .sort((a, b) => b.frac - a.frac)
  for (let k = 0; k < byFraction.length && remainder > 0; k++) {
    out[byFraction[k].i].percent += 1
    remainder -= 1
  }

  return out.filter(m => m.percent > 0).sort((a, b) => b.percent - a.percent)
}

function normalizeMethod(raw: string): string {
  if (/crypto/i.test(raw)) return 'Cryptocurrency'
  if (/wire|ach/i.test(raw)) return 'Wire / ACH'
  if (/debit|credit/i.test(raw)) return 'Debit / Credit'
  if (/peer/i.test(raw)) return 'Peer-to-peer'
  if (/gift/i.test(raw)) return 'Gift cards'
  return raw
}
